import { useEffect, useReducer, useRef } from 'react'
import { fnWorker } from 'worker-mate'

export type UnknownDataResponseType =
  | Array<unknown>
  | Record<string, unknown>
  | undefined
export interface FetchWorkerBaseRequestType {
  options?: RequestInit | undefined
  maxAge?: number
  preferUseCache?: boolean
  middleware?: (data: UnknownDataResponseType) => UnknownDataResponseType
  url: RequestInfo | URL
}
export interface FetchWorkerRequestType
  extends Omit<FetchWorkerBaseRequestType, 'middleware'> {
  middleware?: string
}

export interface FetchWorkerProps extends FetchWorkerBaseRequestType {
  /* 
    update will be fetched after the response is received.
    This is useful for updating after POST PUT or DELETE, 
    where the api doesn't return all the data and the GET
    endpoint is different.
    */
  update?: {
    url: RequestInfo | URL
    options?: RequestInit | undefined
  }
}

export interface StateType {
  data: UnknownDataResponseType
  controller?: AbortController
  error?: Error
  statusCode?: number
  loading: boolean
}

export const initialState: StateType = {
  data: undefined,
  error: undefined,
  loading: false
}

export function reducer(state: StateType, action: any) {
  return { ...state, ...action }
}

export function useFetch(resetFn?: () => void) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const controller = useRef(new AbortController())
  const fetchWorker = async ({
    url,
    options,
    middleware
  }: FetchWorkerProps) => {
    controller.current = new AbortController()
    const signal = controller.current.signal
    dispatch({ loading: true })
    fetch(url.toString(), { ...options!, signal })
      .then((response) => {
        dispatch({ statusCode: response.status })
        if (response.status === 403) {
          resetFn && resetFn()
          window.location.reload()
          return
        }
        if (response.ok && response.status !== 404) {
          return response?.headers?.get('content-type')?.includes('blob')
            ? response.blob()
            : response.json()
        } else {
          throw new Error('not found')
        }
      })
      .then(async (resData) => {
        let data = resData
        if (middleware) {
          data = await fnWorker(middleware, resData).then((d) => d)
        }
        if (data) {
          dispatch({ error: null, data })
        } else {
          dispatch({ error: new Error('No data found') })
        }
      })
      .catch((error) => {
        dispatch({ error })
      })
      .finally(() => {
        dispatch({ loading: false, controller: null })
      })
  }

  useEffect(() => {
    return () => {
      controller.current?.abort()
    }
  }, [])

  return {
    cancelRequest: () => {
      controller.current?.abort()
    },
    fetchWorker,
    data: state?.data,
    error: state?.error,
    loading: state?.loading,
    statusCode: state?.statusCode
  }
}
